export interface NavItem {
  to: string;
  label: string;
  icon: string;
  adminOnly: boolean;
  trueAdminOnly?: boolean;
  /** Ẩn hoàn toàn với tài khoản "Nhân viên" (chỉ Admin / Quản lý kho thấy) */
  employeeHidden?: boolean;
  /** Có trang con (VD: "Kiểm kê" -> "Lịch sử kiểm kê") thì chỉ sáng khi đúng đường dẫn */
  hasChild: boolean;
}

interface NavItemSeed {
  to: string;
  label: string;
  icon: string;
  adminOnly: boolean;
  trueAdminOnly?: boolean;
  employeeHidden?: boolean;
}

const navItemSeeds: NavItemSeed[] = [
  { to: "/tong-quan", label: "Tổng quan", icon: "ri-dashboard-3-line", adminOnly: false },
  { to: "/scan", label: "Quét hàng", icon: "ri-scan-line", adminOnly: false },
  { to: "/inbound", label: "Nhập kho", icon: "ri-inbox-archive-line", adminOnly: false, employeeHidden: true },
  { to: "/nhap-hoan", label: "Nhập hàng hoàn", icon: "ri-arrow-go-back-line", adminOnly: false },
  { to: "/products", label: "Sản phẩm", icon: "ri-archive-line", adminOnly: false },
  { to: "/combos", label: "Combo", icon: "ri-stack-line", adminOnly: false },
  { to: "/vat-tu", label: "Vật tư đóng gói", icon: "ri-inbox-2-line", adminOnly: false },
  { to: "/vat-tu/kiem-ke", label: "Kiểm kê vật tư", icon: "ri-inbox-unarchive-line", adminOnly: false, employeeHidden: true },
  { to: "/kiem-ke", label: "Kiểm kê", icon: "ri-clipboard-line", adminOnly: false, employeeHidden: true },
  { to: "/kiem-ke/lich-su", label: "Lịch sử kiểm kê", icon: "ri-history-line", adminOnly: false, employeeHidden: true },
  { to: "/dashboard", label: "Báo cáo", icon: "ri-bar-chart-box-line", adminOnly: false },
  { to: "/handover", label: "Biên bản bàn giao", icon: "ri-file-shield-2-line", adminOnly: false, employeeHidden: true },
  { to: "/kinh-doanh", label: "Kinh doanh", icon: "ri-funds-line", adminOnly: false, trueAdminOnly: true },
  { to: "/nhan-vien", label: "Nhân viên", icon: "ri-team-line", adminOnly: true },
  { to: "/nhan-vien/bao-cao", label: "Báo cáo nhân viên", icon: "ri-line-chart-line", adminOnly: false },
  { to: "/luu-tru-video", label: "Lịch sử lưu trữ", icon: "ri-hard-drive-3-line", adminOnly: false, employeeHidden: true },
];

const bottomNavItemSeeds: NavItemSeed[] = [
  { to: "/cai-dat-camera", label: "Cài đặt camera", icon: "ri-camera-lens-line", adminOnly: false },
];

/**
 * Tính sẵn cờ "có trang con" một lần duy nhất lúc nạp module (menu tĩnh),
 * thay vì mỗi lần render lại phải quét toàn bộ danh sách.
 */
function withChildFlag(seeds: NavItemSeed[]): NavItem[] {
  return seeds.map((seed) => ({
    ...seed,
    hasChild: seeds.some(
      (other) => other.to !== seed.to && other.to.startsWith(`${seed.to}/`)
    ),
  }));
}

export const navItems: NavItem[] = withChildFlag(navItemSeeds);
export const bottomNavItems: NavItem[] = withChildFlag(bottomNavItemSeeds);

/**
 * Bảng nạp trước (prefetch) mã nguồn của từng trang.
 * Khi người dùng rê chuột / chạm vào một mục menu, đoạn JS của trang đó được
 * tải sẵn nền => lúc bấm vào là mở ngay, không phải chờ tải bundle (hết khựng).
 */
const routeLoaders: Record<string, () => Promise<unknown>> = {
  "/tong-quan": () => import("@/pages/overview/page"),
  "/scan": () => import("@/pages/scan/page"),
  "/inbound": () => import("@/pages/inbound/page"),
  "/nhap-hoan": () => import("@/pages/returns/page"),
  "/products": () => import("@/pages/products/page"),
  "/combos": () => import("@/pages/combos/page"),
  "/vat-tu": () => import("@/pages/materials/page"),
  "/vat-tu/kiem-ke": () => import("@/pages/materials/stocktake/page"),
  "/kiem-ke": () => import("@/pages/stocktake/page"),
  "/kiem-ke/lich-su": () => import("@/pages/stocktake/history/page"),
  "/dashboard": () => import("@/pages/dashboard/page"),
  "/handover": () => import("@/pages/handover/page"),
  "/kinh-doanh": () => import("@/pages/business/page"),
  "/nhan-vien": () => import("@/pages/staff/page"),
  "/nhan-vien/bao-cao": () => import("@/pages/staff/report/page"),
  "/luu-tru-video": () => import("@/pages/videos/storage-history/page"),
  "/cai-dat-camera": () => import("@/pages/camera-settings/page"),
};

const prefetched = new Set<string>();
const prefetching = new Set<string>();

/** Tải trước mã nguồn trang. Gọi nhiều lần cũng chỉ tải một lần duy nhất. */
export function prefetchRoute(path: string) {
  const loader = routeLoaders[path];
  if (!loader || prefetched.has(path) || prefetching.has(path)) return;
  prefetching.add(path);
  loader()
    .then(() => {
      prefetched.add(path);
    })
    .catch(() => {
      // Lỗi mạng thì bỏ qua, lúc bấm vào router sẽ tự tải lại.
    })
    .finally(() => {
      prefetching.delete(path);
    });
}